import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageBreadcrumb from "@/components/base/PageBreadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function AboutPage() {
  usePageMeta(
    "关于我们 - 欧美日韩影视平台",
    "了解欧美日韩影视平台的网站定位、内容范围与使用说明。我们专注日韩与欧美电影电视剧的资讯与内容浏览，服务每一位影视爱好者。",
    "关于我们,欧美日韩,影视平台介绍,使用说明,版权声明",
  );

  const values = [
    {
      icon: "ri-compass-3-line",
      title: "内容定位",
      desc: "专注日韩与欧美地区的电影、电视剧，覆盖科幻、悬疑、剧情、爱情等多种类型。",
    },
    {
      icon: "ri-flashlight-line",
      title: "资讯时效",
      desc: "持续更新票房、上线、拍摄等影视动态，帮助用户第一时间掌握行业最新消息。",
    },
    {
      icon: "ri-layout-grid-line",
      title: "浏览体验",
      desc: "清晰的分类与筛选体系，配合网格与列表混合布局，让查找内容更高效。",
    },
    {
      icon: "ri-emotion-happy-line",
      title: "面向用户",
      desc: "服务追剧用户、影视资讯阅读者以及正在寻找下一部想看作品的观众。",
    },
  ];

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 pt-6">
        <PageBreadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        <section className="mt-5 rounded-lg bg-primary-800 relative overflow-hidden">
          <div className="relative z-10 p-6 md:p-10">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-semibold mb-4">
              <i className="ri-movie-2-fill" />
              欧美日韩影视平台
            </span>
            <h1 className="text-2xl md:text-4xl font-extrabold text-background-50 font-heading leading-tight max-w-2xl">
              一个专注日韩与欧美影视的内容门户
            </h1>
            <p className="mt-4 text-sm md:text-base text-primary-100 leading-relaxed max-w-2xl">
              「欧美日韩」致力于为影视爱好者提供清晰、舒适的内容浏览体验。我们聚合日韩影视与欧美电影电视剧的资讯、分类与详情，让你更轻松地发现下一部想看的作品。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/category/movie"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-film-line" />
                浏览电影
              </Link>
              <Link
                to="/category/tv"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 text-background-50 text-sm font-semibold border border-background-50/25 hover:bg-background-50/25 transition-colors whitespace-nowrap"
              >
                <i className="ri-tv-2-line" />
                浏览电视剧
              </Link>
            </div>
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-5">
            我们的定位与价值
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {values.map((v) => (
              <div
                key={v.title}
                className="bg-background-50 border border-background-200 rounded-lg p-5 card-hover"
              >
                <span className="w-11 h-11 rounded-md bg-primary-50 flex items-center justify-center mb-3">
                  <i className={`${v.icon} text-xl text-primary-600`} />
                </span>
                <h3 className="text-base font-bold text-foreground-950 font-heading">
                  {v.title}
                </h3>
                <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-background-50 border border-background-200 rounded-lg p-6">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 font-heading mb-3">
                <i className="ri-book-open-line text-primary-500" />
                使用说明
              </h2>
              <ul className="space-y-3 text-sm text-foreground-700 leading-relaxed">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line text-accent-500 mt-0.5 shrink-0" />
                  通过顶部导航或分类入口，快速进入电影、电视剧与影视资讯频道。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line text-accent-500 mt-0.5 shrink-0" />
                  在分类页使用类型、地区、年份等条件组合筛选，并按最新、热门或评分排序。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line text-accent-500 mt-0.5 shrink-0" />
                  进入影视详情页可查看完整资料、剧集列表与相关推荐。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line text-accent-500 mt-0.5 shrink-0" />
                  使用顶部搜索框可以检索影片、剧集与资讯关键词。
                </li>
              </ul>
            </div>

            <div className="bg-background-50 border border-background-200 rounded-lg p-6">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 font-heading mb-3">
                <i className="ri-copyright-line text-primary-500" />
                版权声明
              </h2>
              <p className="text-sm text-foreground-600 leading-loose">
                本站为影视内容展示演示站点，页面中的影视名称、简介与资讯内容仅用于功能与设计演示，不提供任何在线播放、下载或付费服务。相关影视作品的著作权归各自权利人所有。若涉及权利问题，请通过下方联系方式与我们沟通。
              </p>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="bg-secondary-50 border border-secondary-100 rounded-lg p-6">
              <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950 font-heading mb-3">
                <i className="ri-contacts-book-line text-secondary-600" />
                联系方式
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                当前站点处于演示状态，以下联系方式仅作展示用途，暂未开通真实服务。
              </p>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2 text-foreground-700">
                  <i className="ri-mail-line text-secondary-600" />
                  联系邮箱：contact@demo-media.example
                </li>
                <li className="flex items-center gap-2 text-foreground-700">
                  <i className="ri-phone-line text-secondary-600" />
                  联系电话：暂未开通
                </li>
                <li className="flex items-center gap-2 text-foreground-700">
                  <i className="ri-map-pin-line text-secondary-600" />
                  办公地址：暂未开通
                </li>
              </ul>
            </div>

            <div className="bg-primary-50 border border-primary-100 rounded-lg p-6">
              <h2 className="text-base font-bold text-foreground-950 font-heading mb-2">
                内容范围
              </h2>
              <div className="flex flex-wrap gap-2">
                {["日韩影视", "欧美电影", "热播剧集", "行业资讯", "票房动态", "高分推荐"].map(
                  (t) => (
                    <span
                      key={t}
                      className="text-xs px-2.5 py-1 rounded-md bg-background-50 text-primary-700 border border-primary-100"
                    >
                      {t}
                    </span>
                  ),
                )}
              </div>
            </div>
          </aside>
        </section>
      </div>
    </SiteLayout>
  );
}